// src/finance/engine/tax.ts
// Fonctions fiscales pures (spec 2026-08-20 § 1.1). Entrées/sorties en EUR ANNUELS,
// non arrondies — l'arrondi au centime (round2) est réservé aux frontières d'affichage.
import type { FinanceTaxSettings, IrBracket } from './types'

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/** IR au barème progressif pour 1 part, base annuelle. */
function irOnePartAnnual(taxable: number, brackets: IrBracket[]): number {
  let tax = 0
  let previousUpTo = 0
  for (const bracket of brackets) {
    const upper = bracket.upTo ?? Number.POSITIVE_INFINITY
    if (taxable > previousUpTo) {
      tax += (Math.min(taxable, upper) - previousUpTo) * bracket.rate
    }
    previousUpTo = upper
  }
  return tax
}

/**
 * Décote (art. 197, 4-I-a CGI) : si l'IR brut est sous le seuil, on retranche
 * `base − taux × IR brut` ; le résultat ne peut jamais être négatif.
 *
 * Approximation assumée : le moteur ne connaît que `fiscalParts`, pas la situation
 * matrimoniale. On retient le jeu « couple » dès 2 parts, sinon « personne seule ».
 * Un parent isolé avec un enfant a lui aussi 2 parts et relèverait en réalité du
 * jeu « personne seule » — cas non couvert (le foyer visé par l'app est mono-déclarant).
 */
function applyDecote(irGross: number, tax: FinanceTaxSettings, parts: number): number {
  const isCouple = parts >= 2
  const threshold = isCouple ? tax.decoteThresholdCouple : tax.decoteThresholdSingle
  const base = isCouple ? tax.decoteBaseCouple : tax.decoteBaseSingle
  if (irGross >= threshold) return irGross
  const decote = Math.max(0, base - tax.decoteRate * irGross)
  return Math.max(0, irGross - decote)
}

/**
 * IR annuel avec quotient familial, plafonnement puis décote — dans cet ordre
 * (art. 197 CGI : le plafonnement est une modalité de liquidation, la décote
 * s'applique à l'impôt qui en résulte).
 *
 * Plafonnement (double liquidation, BOI-IR-LIQ-20-20-20) : on compare l'impôt à
 * parts réelles à l'impôt de référence (1 part si parts < 2, 2 parts sinon) ; si
 * l'avantage tiré des demi-parts additionnelles dépasse `halfPartCap` par demi-part,
 * l'impôt dû est ramené à `référence − plafond`.
 *
 * CONSÉQUENCE ASSUMÉE, à dire explicitement : à `parts === 2` exactement, il n'y a
 * aucune demi-part au-delà de la référence, donc AUCUN plafonnement n'est appliqué.
 * C'est le droit pour un couple sans personne à charge ; ce ne l'est pas pour un
 * parent isolé, dont la référence légale reste 1 part avec un plafond de 2 ×
 * `halfPartCap` (mesuré sur 60 000 € imposables : 4 207,98 € rendus contre
 * 7 489,99 € dus, soit −3 282,01 €/an). Distinguer les deux exigerait une situation
 * matrimoniale que le moteur n'a pas ; le panneau fiscal l'annonce à l'écran et
 * l'infobulle du champ « Parts fiscales » le chiffre.
 *
 * `parts` est explicite (le suivi peut simuler un autre quotient que celui des
 * settings) et vaut par défaut `tax.fiscalParts`.
 */
export function irAnnual(
  taxableAnnual: number,
  tax: FinanceTaxSettings,
  parts: number = tax.fiscalParts,
): number {
  if (taxableAnnual <= 0 || parts <= 0) return 0
  const irAt = (p: number): number => irOnePartAnnual(taxableAnnual / p, tax.irBrackets) * p

  const referenceParts = parts < 2 ? 1 : 2
  const irReference = irAt(referenceParts)
  const irQuotient = irAt(parts)
  const extraHalfParts = Math.max(0, (parts - referenceParts) * 2)
  const cap = tax.halfPartCap * extraHalfParts
  const advantage = irReference - irQuotient
  const irCapped = advantage > cap ? irReference - cap : irQuotient

  return applyDecote(Math.max(0, irCapped), tax, parts)
}

/**
 * UK Corporation Tax annuelle : small profits rate ≤ seuil bas, main rate ≥ seuil haut,
 * marginal relief entre les deux : profit × main − (upper − profit) × fraction.
 *
 * La fraction est DÉRIVÉE des quatre paramètres éditables et non lue dans les settings
 * (`ctMarginalReliefFraction` est conservé mais déprécié) : c'est la seule valeur qui
 * raccorde la courbe au seuil bas. Avec une fraction figée, changer un taux dans le
 * panneau fiscal créait un saut d'impôt au seuil et brisait la monotonie dont dépend
 * la bissection de findRevenueForNet.
 * Vérification : à main = small, la fraction s'annule et la courbe devient linéaire.
 */
export function corporationTaxUkAnnual(profitAnnual: number, tax: FinanceTaxSettings): number {
  if (profitAnnual <= 0) return 0
  if (profitAnnual <= tax.ctLowerLimit) return profitAnnual * tax.ctSmallRate
  if (profitAnnual >= tax.ctUpperLimit) return profitAnnual * tax.ctMainRate
  const span = tax.ctUpperLimit - tax.ctLowerLimit
  if (span <= 0) return profitAnnual * tax.ctMainRate
  const fraction = ((tax.ctMainRate - tax.ctSmallRate) * tax.ctLowerLimit) / span
  return profitAnnual * tax.ctMainRate - (tax.ctUpperLimit - profitAnnual) * fraction
}

/**
 * IS français : palier progressif simple — taux réduit PME jusqu'au plafond,
 * taux normal sur l'excédent (pas de marginal relief à la britannique).
 */
export function corporationTaxFrAnnual(profitAnnual: number, tax: FinanceTaxSettings): number {
  if (profitAnnual <= 0) return 0
  if (profitAnnual <= tax.frIsReducedCap) return profitAnnual * tax.frIsReducedRate
  return tax.frIsReducedCap * tax.frIsReducedRate + (profitAnnual - tax.frIsReducedCap) * tax.frIsMainRate
}

/** Abattement forfaitaire frais pro : rate × base, plafonné (minimum légal ignoré en v1). */
export function salaryAllowanceAnnual(netBeforeIrAnnual: number, tax: FinanceTaxSettings): number {
  if (netBeforeIrAnnual <= 0) return 0
  return Math.min(netBeforeIrAnnual * tax.salaryAllowanceRate, tax.salaryAllowanceCap)
}
